/**
 * SpaceHelpers - Centralized space management for frontend
 * This is the single source of truth for space operations in the frontend.
 */
import { API_BASE_URL } from '../api/config';
import { createFileLogger } from '../utils/logger';
import useSystemStore from '../stores/systemStore';

const log = createFileLogger('SPACE_HELPERS');

const DESKTOP_NUMBER_PATTERN = /^Desktop (\d+)$/;

/**
 * Returns the next auto-generated desktop name ("Desktop 1", "Desktop 2", ...)
 * — one past the highest "Desktop N" name already in the switcher's
 * `recentSpaces` cache. Read synchronously from `systemStore` (populated by
 * `refreshRecentSpaces`/`ensureRecentSpaces`) so every "New Desktop" creation
 * call site can compute a name without an extra round trip.
 */
export function getNextDesktopName(): string {
  const { recentSpaces } = useSystemStore.getState();
  const highest = recentSpaces.reduce((max, space) => {
    const match = DESKTOP_NUMBER_PATTERN.exec(space.name || '');
    return match ? Math.max(max, parseInt(match[1], 10)) : max;
  }, 0);
  return `Desktop ${highest + 1}`;
}

export interface EnsureSpaceOptions {
  authToken: string;
  createIfMissing?: boolean;  // Default true
  metadata?: Record<string, any>;
  spaceName?: string;  // Optional custom name
  settings?: Record<string, any>;  // Optional custom settings
}

export interface EnsureSpaceResult {
  spaceId: string;
  wasCreated: boolean;  // True if we created it, false if it existed
}

/**
 * Ensures user has a space - returns existing or creates new one.
 * This is the ONLY way tools and flows should get/create spaces.
 * 
 * Implements the "Get or Create" pattern:
 * - If user has spaces: Returns first space ID
 * - If user has no spaces: Creates default space and returns ID
 * 
 * @param options - Configuration options
 * @returns Promise with space ID and whether it was created
 * @throws Error if space operations fail
 * 
 * @example
 * const { spaceId, wasCreated } = await ensureUserSpace({
 *   authToken: token,
 *   metadata: { created_via: 'blog_creator' }
 * });
 */
export async function ensureUserSpace(
  options: EnsureSpaceOptions
): Promise<EnsureSpaceResult> {
  const { 
    authToken, 
    createIfMissing = true, 
    metadata, 
    spaceName,
    settings 
  } = options;
  
  log.log('[ensureUserSpace] Checking for existing spaces...');
  
  // Step 1: Check for existing spaces. owned_only=true excludes Spaces the caller
  // can merely access via a direct or org-wide ACL grant (e.g. a teammate's
  // shared Desktop) — this is used for landing/desktop-resolution, where a
  // shared Space must never be treated as the caller's own.
  const spacesResponse = await fetch(`${API_BASE_URL}/spaces/list?owned_only=true`, {
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!spacesResponse.ok) {
    throw new Error(`Failed to fetch user spaces: ${spacesResponse.status}`);
  }
  
  const spaces = await spacesResponse.json();
  
  // Step 2: Return existing space (but update settings if provided)
  if (spaces && spaces.length > 0) {
    const spaceId = spaces[0]._id || spaces[0].id;
    log.log(`[ensureUserSpace] User already has ${spaces.length} space(s). Using: ${spaceId}`);
    
    // ✅ NEW: Update space settings if provided (so existing users get tool-specific model)
    if (settings) {
      log.log('[ensureUserSpace] Updating existing space settings for tool-specific configuration...');
      log.log('[ensureUserSpace] Settings to update:', JSON.stringify(settings));
      try {
        const url = `${API_BASE_URL}/spaces/${spaceId}`;
        log.log('[ensureUserSpace] PUT URL:', url);
        
        const updateResponse = await fetch(url, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            settings: settings
          })
        });
        
        log.log('[ensureUserSpace] Response status:', updateResponse.status);
        
        if (updateResponse.ok) {
          log.log('[ensureUserSpace] Settings updated successfully');
          
          // Refetch the updated space and update spaceStore
          // This ensures initializeFromSpaceSettings reads fresh data
          try {
            const updatedSpaceResponse = await updateResponse.json();
            log.log('[ensureUserSpace] Updating spaceStore with fresh space data');
            
            // Dynamically import spaceStore to avoid circular dependencies
            const { default: useSpaceStore } = await import('../stores/spaceStore');
            const spaceStore = useSpaceStore.getState();
            
            // Update the activeSpace in spaceStore with the fresh data
            if (spaceStore.activeSpace) {
              // If activeSpace exists, MERGE to preserve existing fields
              const mergedSpace = {
                ...spaceStore.activeSpace,
                ...updatedSpaceResponse,
                id: spaceStore.activeSpace.id || updatedSpaceResponse._id || spaceId,
                settings: updatedSpaceResponse.settings || spaceStore.activeSpace.settings
              };
              
              useSpaceStore.setState({ activeSpace: mergedSpace });
              log.log('[ensureUserSpace] SpaceStore merged successfully');
            } else {
              // If activeSpace doesn't exist (user on landing page), SET it directly
              const freshSpace = {
                ...updatedSpaceResponse,
                id: updatedSpaceResponse._id || spaceId
              };
              
              useSpaceStore.setState({ activeSpace: freshSpace });
              log.log('[ensureUserSpace] SpaceStore set successfully');
            }
          } catch (refreshErr) {
            log.error('[ensureUserSpace] Failed to refresh spaceStore after update:', refreshErr);
            // Don't throw - the backend update succeeded, so we can continue
          }
        } else {
          const errorText = await updateResponse.text();
          log.error(`[ensureUserSpace] Failed to update settings: ${updateResponse.status}`, errorText);
        }
      } catch (err) {
        log.error('[ensureUserSpace] Error updating settings:', err);
        // Don't throw - settings update is optional, user can still use the space
      }
    }
    
    return {
      spaceId,
      wasCreated: false
    };
  }
  
  // Step 3: Create new space if none exist
  if (!createIfMissing) {
    throw new Error('User has no spaces and createIfMissing=false');
  }
  
  log.log('[ensureUserSpace] No spaces found. Creating default space...');
  
  const defaultSettings = {
    spaceChat: {
      model: 'expert',
      widths: {
        focused: 600,
        spatial: 600
      }
    }
  };
  
  // Merge custom settings if provided
  const finalSettings = settings ? { ...defaultSettings, ...settings } : defaultSettings;

  // Resolve org_id from the active dataspace context (mirrors systemStore.createSpace)
  let orgId: string | null = null;
  try {
    const { useDataspaceContextStore } = await import('../stores/dataspaceContextStore');
    const ctx = useDataspaceContextStore.getState().currentContext;
    orgId = ctx.type === 'org' ? ctx.orgId : null;
  } catch {
    // Store not yet initialized — proceed without org_id
  }

  log.log('[ensureUserSpace] Resolved org_id for new space:', orgId);
  
  const resolvedName = spaceName || getNextDesktopName();

  const spaceResponse = await fetch(`${API_BASE_URL}/spaces`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      name: resolvedName,
      templateType: null,
      settings: finalSettings,
      metadata: metadata || {},
      ...(orgId ? { org_id: orgId } : {})
    })
  });
  
  if (!spaceResponse.ok) {
    throw new Error(`Failed to create space: ${spaceResponse.status}`);
  }
  
  const space = await spaceResponse.json();
  const spaceId = space._id || space.id;
  
  log.log(`[ensureUserSpace] Created new space: ${spaceId} (name: "${resolvedName}")`);
  
  return {
    spaceId,
    wasCreated: true
  };
}

/**
 * Resolves the Desktop a user should land on right now: their most recently
 * active Desktop in the current dataspace context (scoped to them via
 * ownership/ACL — never a shared "Root Space"), creating one if they don't
 * have any yet. This is the single landing-destination resolver used by every
 * flow that used to fall back to the old `/home` dashboard route.
 */
export async function resolveLandingDesktopRoute(authToken?: string): Promise<string> {
  const token = authToken || localStorage.getItem('authToken');
  if (!token) {
    throw new Error('resolveLandingDesktopRoute: no auth token available');
  }
  const { spaceId } = await ensureUserSpace({ authToken: token });
  return `/desktops/${spaceId}`;
}

